/**
 * Shared utilities for chat components
 * Reduces code duplication between chat implementations
 */

import { MatSnackBar } from "@angular/material/snack-bar";
import { Bot } from "../../core/models";
import { Message } from "../../core/models/message.model";
import { copyToClipboard } from "./utils";

/**
 * Get bot gradient style object
 */
export function getBotGradientStyleUtil(bot?: Bot | null | any): any {
  if (!bot?.gradientColors) return {};

  return {
    background: `linear-gradient(135deg, 
      ${bot.gradientColors.start} 0%, 
      ${bot.gradientColors.middle} 50%, 
      ${bot.gradientColors.end} 100%)`,
  };
}

/**
 * Get status indicator color class
 */
export function getStatusColorClass(bot?: Bot | null): string {
  if (!bot) return "bg-gray-400";

  switch (bot.status) {
    case "online":
      return "bg-green-500";
    case "busy":
      return "bg-yellow-500";
    case "offline":
      return "bg-gray-400";
    default:
      return "bg-gray-400";
  }
}

/**
 * Format chat history for clipboard export
 */
export async function copyFormattedChatHistory(
  messages: Message[],
  sessionId: string | undefined,
  additionalInfo: { [key: string]: string },
  snackBar: MatSnackBar
): Promise<void> {
  if (!sessionId) {
    snackBar.open('Error: No hay sesión activa', 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
    return;
  }
  const currentDate = new Date().toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  // Build header
  let header = `Session ID: ${sessionId}\n`;
  for (const [key, value] of Object.entries(additionalInfo)) {
    header += `${key}: ${value}\n`;
  }
  header += `Fecha: ${currentDate}\n\n`;

  // Format messages
  const formattedChat = messages
    .map((entry: Message) => {
      const role = entry.isUser ? 'user' : 'assistant';
      const timestamp = new Date(entry.timestamp).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `[${timestamp}] ${role}: ${entry.content}`;
    })
    .join('\n');

  const fullChat = header + formattedChat;

  if (await copyToClipboard(fullChat)) {
    snackBar.open('Historial copiado al portapapeles', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar'],
    });
  } else {
    snackBar.open('Error al copiar el historial', 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }
}

/**
 * Scroll container to bottom
 */
export function scrollContainerToBottom(container: HTMLElement | null, delay = 100): void {
  if (!container) return;
  
  setTimeout(() => {
    container.scrollTop = container.scrollHeight;
  }, delay);
}
